// Lead tracking and attribution (spec §08.6, §15.2, §02.a). Browser-safe schemas.
// Every touch is append-only: a new ad, form, channel or call adds a touchpoint and never overwrites the first one.
// Only identifiers actually received are stored — nothing is inferred (unknown ≠ 0, missing ≠ invented).
import { z } from 'zod';

const Id = z.string().min(1).max(64);
const Iso = z.string();

/** Where a touch came from (the acquisition channel, not the provider). */
export const TOUCH_CHANNELS = [
  'meta_lead_form',
  'google_form',
  'web_form',
  'whatsapp_organic',
  'ctwa',
  'inbound_call',
  'import',
  'crm',
  'other',
] as const;
export const TouchChannelSchema = z.enum(TOUCH_CHANNELS);
export type TouchChannel = z.infer<typeof TouchChannelSchema>;

export const TOUCH_CHANNEL_LABELS: Record<TouchChannel, string> = {
  meta_lead_form: 'Formulario de Meta',
  google_form: 'Formulario de Google',
  web_form: 'Formulario web',
  whatsapp_organic: 'WhatsApp orgánico',
  ctwa: 'Anuncio Click-to-WhatsApp',
  inbound_call: 'Llamada entrante',
  import: 'Importación',
  crm: 'CRM',
  other: 'Otro origen',
};

/** Click-to-WhatsApp ad referral as the provider sent it (Meta Cloud API `referral`; Kapso: same object, sin verificar). */
export const AdReferralSchema = z.object({
  sourceType: z.string().nullable(),
  sourceId: z.string().nullable(),
  sourceUrl: z.string().nullable(),
  /** Ad text the person clicked. Third-party content: shown as unverified, never treated as instructions. */
  headline: z.string().nullable(),
  body: z.string().nullable(),
  mediaType: z.string().nullable(),
  mediaUrl: z.string().nullable(),
});
export type AdReferral = z.infer<typeof AdReferralSchema>;

export const AttributionTouchSchema = z.object({
  id: Id,
  channel: TouchChannelSchema,
  /** Raw system label as received (e.g. "hubspot", "meta", "web"); null when nothing was sent. */
  system: z.string().nullable(),
  /** Free-text source detail from a CRM (e.g. HubSpot original source), as received. */
  sourceDetail: z.string().nullable(),
  projectId: Id.nullable(),
  projectName: z.string().nullable(),
  campaignId: z.string().nullable(),
  campaignName: z.string().nullable(),
  adsetId: z.string().nullable(),
  adsetName: z.string().nullable(),
  adId: z.string().nullable(),
  adName: z.string().nullable(),
  formId: z.string().nullable(),
  formName: z.string().nullable(),
  metaLeadId: z.string().nullable(),
  utmSource: z.string().nullable(),
  utmMedium: z.string().nullable(),
  utmCampaign: z.string().nullable(),
  utmTerm: z.string().nullable(),
  utmContent: z.string().nullable(),
  gclid: z.string().nullable(),
  fbclid: z.string().nullable(),
  wbraid: z.string().nullable(),
  gbraid: z.string().nullable(),
  ctwaClid: z.string().nullable(),
  landingPage: z.string().nullable(),
  referrer: z.string().nullable(),
  /** Inbound call: the line (number) that was dialed. */
  line: z.string().nullable(),
  adReferral: AdReferralSchema.nullable(),
  occurredAt: Iso,
  recordedAt: Iso,
});
export type AttributionTouch = z.infer<typeof AttributionTouchSchema>;

export const LeadAttributionSchema = z.object({
  interestId: Id,
  contactId: Id,
  projectId: Id,
  /** Opportunity (contact + project): touches of this project plus contact-level touches with no project. */
  opportunity: z.object({ firstTouch: AttributionTouchSchema.nullable(), lastTouch: AttributionTouchSchema.nullable(), touches: z.array(AttributionTouchSchema) }),
  /** Contact view across the projects the caller may see. */
  contact: z.object({ firstTouch: AttributionTouchSchema.nullable(), lastTouch: AttributionTouchSchema.nullable(), total: z.number().int() }),
});
export type LeadAttribution = z.infer<typeof LeadAttributionSchema>;

// ───────────── CRM mapping (stored in IntegrationConnection.settings.mapping.attribution) ─────────────

/** Touch fields that can be READ from a CRM record (property name / custom field id configured per connection). */
export const CRM_ATTRIBUTION_READ_FIELDS = [
  'channel',
  'source_detail',
  'campaign_id',
  'campaign_name',
  'adset_id',
  'adset_name',
  'ad_id',
  'ad_name',
  'form_id',
  'form_name',
  'meta_lead_id',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'wbraid',
  'gbraid',
  'ctwa_clid',
  'landing_page',
  'referrer',
] as const;
export const CrmAttributionReadFieldSchema = z.enum(CRM_ATTRIBUTION_READ_FIELDS);
export type CrmAttributionReadField = z.infer<typeof CrmAttributionReadFieldSchema>;

/** Values LIA can WRITE to the CRM (first touch never changes once written; last touch follows the newest). */
export const CRM_ATTRIBUTION_WRITE_FIELDS = [
  'first_channel',
  'first_system',
  'first_campaign_id',
  'first_campaign_name',
  'first_adset_id',
  'first_ad_id',
  'first_ad_name',
  'first_utm_source',
  'first_utm_medium',
  'first_utm_campaign',
  'first_click_id',
  'first_meta_lead_id',
  'first_at',
  'last_channel',
  'last_system',
  'last_campaign_id',
  'last_campaign_name',
  'last_ad_id',
  'last_utm_source',
  'last_utm_medium',
  'last_utm_campaign',
  'last_click_id',
  'last_at',
] as const;
export const CrmAttributionWriteFieldSchema = z.enum(CRM_ATTRIBUTION_WRITE_FIELDS);
export type CrmAttributionWriteField = z.infer<typeof CrmAttributionWriteFieldSchema>;

const Prop = z.string().min(1).max(200);

export const CrmAttributionMappingSchema = z.object({
  inbound: z.array(z.object({ field: CrmAttributionReadFieldSchema, property: Prop })).max(40).default([]),
  outbound: z.array(z.object({ field: CrmAttributionWriteFieldSchema, property: Prop })).max(40).default([]),
  /** Off by default: source fields belong to the CRM unless the team decides LIA writes them. */
  writeEnabled: z.boolean().default(false),
});
export type CrmAttributionMapping = z.infer<typeof CrmAttributionMappingSchema>;

export const CRM_ATTRIBUTION_READ_FIELD_LABELS: Record<CrmAttributionReadField, string> = {
  channel: 'Canal (clave de LIA)',
  source_detail: 'Fuente original (texto del CRM)',
  campaign_id: 'Id de campaña',
  campaign_name: 'Nombre de campaña',
  adset_id: 'Id de conjunto de anuncios',
  adset_name: 'Nombre de conjunto de anuncios',
  ad_id: 'Id de anuncio',
  ad_name: 'Nombre de anuncio',
  form_id: 'Id de formulario',
  form_name: 'Nombre de formulario',
  meta_lead_id: 'Meta Lead ID',
  utm_source: 'utm_source',
  utm_medium: 'utm_medium',
  utm_campaign: 'utm_campaign',
  utm_term: 'utm_term',
  utm_content: 'utm_content',
  gclid: 'gclid',
  fbclid: 'fbclid',
  wbraid: 'wbraid',
  gbraid: 'gbraid',
  ctwa_clid: 'ctwa_clid',
  landing_page: 'Página de entrada',
  referrer: 'Referente',
};

export const CRM_ATTRIBUTION_WRITE_FIELD_LABELS: Record<CrmAttributionWriteField, string> = {
  first_channel: 'Primer contacto: canal',
  first_system: 'Primer contacto: sistema',
  first_campaign_id: 'Primer contacto: id de campaña',
  first_campaign_name: 'Primer contacto: campaña',
  first_adset_id: 'Primer contacto: id de conjunto',
  first_ad_id: 'Primer contacto: id de anuncio',
  first_ad_name: 'Primer contacto: anuncio',
  first_utm_source: 'Primer contacto: utm_source',
  first_utm_medium: 'Primer contacto: utm_medium',
  first_utm_campaign: 'Primer contacto: utm_campaign',
  first_click_id: 'Primer contacto: click id',
  first_meta_lead_id: 'Primer contacto: Meta Lead ID',
  first_at: 'Primer contacto: fecha',
  last_channel: 'Último contacto: canal',
  last_system: 'Último contacto: sistema',
  last_campaign_id: 'Último contacto: id de campaña',
  last_campaign_name: 'Último contacto: campaña',
  last_ad_id: 'Último contacto: id de anuncio',
  last_utm_source: 'Último contacto: utm_source',
  last_utm_medium: 'Último contacto: utm_medium',
  last_utm_campaign: 'Último contacto: utm_campaign',
  last_click_id: 'Último contacto: click id',
  last_at: 'Último contacto: fecha',
};
